import { useDraggable } from '@dnd-kit/react';
import { isNotDefined } from '@sgme/fp';
import { VStack } from '@sgme/ui';
import clsx from 'clsx';
import { type CSSProperties, memo, type PropsWithChildren, useEffect, useMemo, useRef } from 'react';

import { useTileHeightObserver } from '@/App/Grid/tileHeightObserverContext.tsx';
import { UnauthorizedTile } from '@/App/Grid/UnauthorizedTile.tsx';
import { CashProvider } from '@/App/tiles/CashTile/CashContext.tsx';
import { CashTile } from '@/App/tiles/CashTile/index.tsx';
import { FlexiForwardContext } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardTile } from '@/App/tiles/FlexiForward/index.tsx';
import { ProductProvider as ForwardAccumulatorProvider } from '@/App/tiles/ForwardAccumulator/ProductProvider.tsx';
import { Tile as ForwardAccumulatorTile } from '@/App/tiles/ForwardAccumulator/Tile.tsx';
import { OptionContext } from '@/App/tiles/Option/OptionContext.ts';
import { OptionTile } from '@/App/tiles/Option/OptionTile.tsx';
import { QueueContextProvider } from '@/App/tiles/Option/QueueContext.tsx';

import './DraggableTile.css';
import { AlgoProvider } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import { AlgoOrderTile } from '@/App/tiles/orders/AlgoOrders/index.tsx';
import { CallOrderProvider } from '@/App/tiles/orders/CallOrder/CallOrderCreationTile/CallOrderContext.tsx';
import { CallOrderTile } from '@/App/tiles/orders/CallOrder/index.tsx';
import { FixingProvider } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { FixingOrderTile } from '@/App/tiles/orders/FixingOrder/index.tsx';
import { StopLossOrderTile } from '@/App/tiles/orders/StopLossOrder/index.tsx';
import { StopLossProvider } from '@/App/tiles/orders/StopLossOrder/StopLossOrderCreationTile/stopLossContext.tsx';
import { TakeProfitOrderTile } from '@/App/tiles/orders/TakeProfitOrder/index.tsx';
import { TakeProfitProvider } from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext.tsx';
import { SwapTile } from '@/App/tiles/Swap/index.tsx';
import { SwapProvider } from '@/App/tiles/Swap/SwapContext.tsx';
import { TargetAccumulatorTile } from '@/App/tiles/TargetAccumulator/index.tsx';
import { TargetAccumulatorProvider } from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import { TileCatchAll } from '@/components/fatality/TileCatchAll.tsx';
import { MIN_TILE_HEIGHT, TILE_OUTER_PADDING } from '@/config/grid.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { selectTileClientId, selectTileProductName, selectTileProductType } from '@/store/tiles/selectors.ts';
import { hasAccessToTileProduct } from '@/store/tiles/utils.ts';
import { gridToPx } from '@/utils/grid/coordinates.ts';
import { ZINDEX_TILE } from '@/utils/grid/zIndices.ts';

import { DragBar } from './DragBar.tsx';

export type DraggableTileProps = {
  tileId: string;
  x: number;
  y: number;
  w: number;
  h: number;
};

export const DraggableTile = memo((props: PropsWithChildren<DraggableTileProps>) => {
  const { tileId, h, w, x, y } = props;
  const productType = useAppSelector((state) => selectTileProductType(state, tileId));
  const clientId = useAppSelector((state) => selectTileClientId(state, tileId));
  const productName = useAppSelector(selectTileProductName(tileId));
  const {
    currentUser: { clients, productsAccess },
  } = useUserContext();

  const heightObservedRef = useRef<HTMLDivElement>(null);
  const heightObserver = useTileHeightObserver();

  useEffect(() => {
    if (!heightObservedRef.current) {
      return undefined;
    }
    heightObserver.observe(heightObservedRef.current);
    return () => {
      if (heightObservedRef.current) {
        heightObserver.unobserve(heightObservedRef.current);
      }
    };
  }, [heightObserver.observe, heightObserver.unobserve]);

  const { isDragging, ref, handleRef } = useDraggable({
    id: tileId,
    type: 'tile',
  });

  const hasAccessToTileClient = useMemo(
    () => isNotDefined(clientId) || clients.some((client) => client.companyId === clientId),
    [clientId, clients],
  );

  const hasAccessToProduct = useMemo(
    () => hasAccessToTileProduct(productName, productsAccess),
    [productsAccess, productName],
  );

  const { x: left, y: top } = gridToPx({ col: x, row: y });
  const { x: width } = gridToPx({ col: w, row: h });

  const tileOuterBoxStyle: CSSProperties = {
    position: 'absolute',
    left,
    top,
    width: `${width}px`,
    padding: `${TILE_OUTER_PADDING}px`,
    userSelect: 'none',
    zIndex: ZINDEX_TILE,
  };

  return (
    <div data-e2e="tile" data-tile-id={tileId} ref={ref} style={tileOuterBoxStyle}>
      <VStack
        ref={heightObservedRef}
        className={clsx('tile-outer-layout', 'transparent', 'bg-lvl1', 'border-primary', 'shadow')}
        verticalAlignment="start"
        style={{ minHeight: `${MIN_TILE_HEIGHT - 2 * TILE_OUTER_PADDING}px` }}
      >
        <DragBar isDraggingInProgress={isDragging} ref={handleRef} />

        {hasAccessToTileClient && hasAccessToProduct ? (
          <TileCatchAll tileId={tileId}>
            {productType === 'Cash' && (
              <CashProvider tileId={tileId}>
                <CashTile key={tileId} tileId={tileId} />
              </CashProvider>
            )}

            {productType === 'Swap' && (
              <SwapProvider tileId={tileId}>
                <SwapTile key={tileId} tileId={tileId} />
              </SwapProvider>
            )}

            {productType === 'Option' && (
              <OptionContext value={tileId}>
                <QueueContextProvider>
                  <OptionTile tileId={tileId} />
                </QueueContextProvider>
              </OptionContext>
            )}

            {productType === 'FlexiForward' && (
              <FlexiForwardContext value={tileId}>
                <FlexiForwardTile key={tileId} tileId={tileId} />
              </FlexiForwardContext>
            )}

            {productType === 'TargetAccumulator' && (
              <TargetAccumulatorProvider tileId={tileId}>
                <TargetAccumulatorTile tileId={tileId} />
              </TargetAccumulatorProvider>
            )}

            {productType === 'ForwardAccumulator' && (
              <ForwardAccumulatorProvider tileId={tileId}>
                <ForwardAccumulatorTile />
              </ForwardAccumulatorProvider>
            )}

            {/*limit orders*/}
            {productType === 'TakeProfitOrder' && (
              <TakeProfitProvider tileId={tileId}>
                <TakeProfitOrderTile key={tileId} />
              </TakeProfitProvider>
            )}
            {productType === 'StopLossOrder' && (
              <StopLossProvider tileId={tileId}>
                <StopLossOrderTile key={tileId} />
              </StopLossProvider>
            )}
            {productType === 'CallOrder' && (
              <CallOrderProvider tileId={tileId}>
                <CallOrderTile key={tileId} />
              </CallOrderProvider>
            )}
            {productType === 'FixingOrder' && (
              <FixingProvider tileId={tileId}>
                <FixingOrderTile key={tileId} />
              </FixingProvider>
            )}
            {/*algo orders*/}
            {productType === 'Twap+Order' && (
              <AlgoProvider tileId={tileId}>
                <AlgoOrderTile key={tileId} algoType="Twap+Order" />
              </AlgoProvider>
            )}
            {productType === 'NightjarOrder' && (
              <AlgoProvider tileId={tileId}>
                <AlgoOrderTile key={tileId} algoType="NightjarOrder" />
              </AlgoProvider>
            )}
            {productType === 'FalconOrder' && (
              <AlgoProvider tileId={tileId}>
                <AlgoOrderTile key={tileId} algoType="FalconOrder" />
              </AlgoProvider>
            )}
          </TileCatchAll>
        ) : (
          <UnauthorizedTile tileId={tileId} />
        )}
      </VStack>
    </div>
  );
});
